import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import type { ComboboxOption } from '../../components/Combobox';
import { CustomerCombobox } from '../crm/CustomerCombobox';
import { useTemplates } from '../shortcuts/queries';
import {
  useCreateRecurring,
  useDeleteRecurring,
  useRecurring,
  useRunRecurring,
  useUpdateRecurring,
} from './queries';
import { Select } from '../../components/Select';

/**
 * Wiederkehrende Tickets -- F3-19, K-24, K-68.
 *
 * Die Liste zeigt beides: `last_created_at` als Nachweis und `next_due_at` als
 * Absicht. Sie fallen auseinander, sobald jemand den Plan aendert, und dann
 * will man beide sehen.
 *
 * Der Vorlauf steht als eigenes Feld daneben, weil er der eigentliche Zweck
 * ist: Eine Wartung, die am Ersten faellig ist, soll sich planen lassen und
 * nicht am Ersten im Posteingang auftauchen.
 */
export function RecurringPanel() {
  const { t } = useTranslation();
  const plans = useRecurring();
  const templates = useTemplates();
  const create = useCreateRecurring();
  const update = useUpdateRecurring();
  const remove = useDeleteRecurring();
  const run = useRunRecurring();

  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [customer, setCustomer] = useState<ComboboxOption | null>(null);
  const customerId = customer?.id ?? '';
  const [cron, setCron] = useState('0 6 1 * *');
  const [leadDays, setLeadDays] = useState('0');

  return (
    <div className="stack">
      <section className="card">
        <h2>{t('automation.newPlan')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="plan-name">
            {t('masterdata.name')}
          </label>
          <input
            id="plan-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="plan-template">
            {t('automation.template')}
          </label>
          <Select
            id="plan-template"
            className="field__control"
            value={templateId}
            onChange={(event) => {
              setTemplateId(event.target.value);
            }}
          >
            <option value="">{t('macros.chooseValue')}</option>
            {(templates.data ?? []).map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="plan-customer">
            {t('automation.customer')}
          </label>
          <CustomerCombobox id="plan-customer" value={customer} onChange={setCustomer} />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="plan-cron">
            {t('automation.cron')}
          </label>
          <input
            id="plan-cron"
            className="field__control"
            value={cron}
            onChange={(event) => {
              setCron(event.target.value);
            }}
          />
          <span className="field__hint">{t('automation.cronHint')}</span>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="plan-lead">
            {t('automation.leadDays')}
          </label>
          <input
            id="plan-lead"
            className="field__control"
            inputMode="numeric"
            value={leadDays}
            onChange={(event) => {
              setLeadDays(event.target.value);
            }}
          />
          <span className="field__hint">{t('automation.leadDaysHint')}</span>
        </div>

        {create.error ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={
            name.trim() === '' || templateId === '' || customerId === '' || create.isPending
          }
          onClick={() => {
            create.mutate(
              {
                name: name.trim(),
                template_id: templateId,
                customer_id: customerId,
                cron_expression: cron.trim(),
                lead_days: Number(leadDays) || 0,
                is_active: true,
              },
              {
                onSuccess: () => {
                  setName('');
                  setLeadDays('0');
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>

      <section className="card">
        <div className="page-header">
          <h2>{t('automation.plans')}</h2>
          <Button
            variant="secondary"
            disabled={run.isPending}
            onClick={() => {
              run.mutate();
            }}
          >
            {t('automation.runNow')}
          </Button>
        </div>

        {run.data ? (
          <p className="table__muted">
            {run.data.length === 0
              ? t('automation.nothingDue')
              : t('automation.plansRan', { count: run.data.length })}
          </p>
        ) : null}
        {run.error ? <ErrorNotice error={run.error} /> : null}

        {plans.isPending ? <LoadingIndicator /> : null}
        {plans.error ? <ErrorNotice error={plans.error} /> : null}
        {plans.data?.length === 0 ? (
          <p className="table__muted">{t('automation.noPlans')}</p>
        ) : null}

        <ul className="task-list">
          {(plans.data ?? []).map((plan) => (
            <li key={plan.id} className="task-list__item task-list__item--column">
              <div className="row">
                <strong>{plan.name}</strong>
                <Badge tone={plan.is_active ? 'success' : 'neutral'}>
                  {plan.is_active ? t('automation.active') : t('automation.inactive')}
                </Badge>
                <code>{plan.cron_expression}</code>
                {plan.lead_days > 0 ? (
                  <Badge tone="info">
                    {t('automation.leadDaysBadge', { count: plan.lead_days })}
                  </Badge>
                ) : null}
              </div>

              <p className="table__muted">
                {plan.next_due_at
                  ? t('automation.nextDue', { when: new Date(plan.next_due_at).toLocaleString() })
                  : t('automation.noNextDue')}
                {plan.last_created_at
                  ? ` · ${t('automation.lastCreated', {
                      when: new Date(plan.last_created_at).toLocaleString(),
                    })}`
                  : ''}
              </p>

              <div className="row">
                <Button
                  variant="ghost"
                  onClick={() => {
                    update.mutate({
                      recurringId: plan.id,
                      payload: { is_active: !plan.is_active },
                    });
                  }}
                >
                  {plan.is_active ? t('automation.deactivate') : t('automation.activate')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(t('masterdata.deleteConfirm'))) {
                      remove.mutate(plan.id);
                    }
                  }}
                >
                  {t('masterdata.delete')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
